import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '../../utils/helpers';
import { t } from '../../i18n';
import { Button } from './Button';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  /** Prevent closing while an action is in progress */
  busy?: boolean;
}

const SIZES = { sm: 'max-w-md', md: 'max-w-lg', lg: 'max-w-2xl', xl: 'max-w-4xl', '2xl': 'max-w-6xl' };

export function Modal({ isOpen, onClose, title, description, children, footer, size = 'md', busy = false }: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen) return;
    const previousFocus = document.activeElement as HTMLElement | null;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onCloseRef.current();
    };
    document.addEventListener('keydown', handleKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Focus the first form control, or the dialog itself
    window.setTimeout(() => {
      const target = dialogRef.current?.querySelector<HTMLElement>(
        'input:not([type=hidden]):not([disabled]), select:not([disabled]), textarea:not([disabled])',
      );
      (target ?? dialogRef.current)?.focus();
    }, 0);

    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = previousOverflow;
      previousFocus?.focus?.();
    };
  }, [isOpen, busy]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-secondary-900/50 backdrop-blur-[1px]" onClick={busy ? undefined : onClose} aria-hidden="true" />
      <div className="flex min-h-full items-start justify-center p-4 sm:items-center">
        <div
          ref={dialogRef}
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
          aria-label={typeof title === 'string' ? title : undefined}
          className={cn('relative w-full rounded-xl bg-white shadow-xl outline-none animate-slide-in', SIZES[size])}
        >
          {title && (
            <div className="flex items-start justify-between gap-4 border-b border-secondary-100 px-5 py-4 sm:px-6">
              <div className="min-w-0">
                <h2 className="text-lg font-semibold text-secondary-900">{title}</h2>
                {description && <p className="mt-0.5 text-sm text-secondary-500">{description}</p>}
              </div>
              <button type="button" onClick={onClose} disabled={busy} className="icon-btn -mr-2" aria-label={t('Zavrieť')}>
                <X className="h-5 w-5" />
              </button>
            </div>
          )}
          <div className="px-5 py-5 sm:px-6">{children}</div>
          {footer && (
            <div className="flex flex-wrap justify-end gap-2 rounded-b-xl border-t border-secondary-100 bg-secondary-50 px-5 py-3 sm:px-6">
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: ReactNode;
  confirmText?: string;
  variant?: 'danger' | 'primary';
  loading?: boolean;
}

export function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = t('Potvrdiť'),
  variant = 'danger',
  loading = false,
}: ConfirmDialogProps) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="sm"
      busy={loading}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            {t('Zrušiť')}
          </Button>
          <Button variant={variant} onClick={onConfirm} loading={loading}>
            {confirmText}
          </Button>
        </>
      }
    >
      <div className="text-secondary-600">{message}</div>
    </Modal>
  );
}
